import { useState, useEffect, useRef } from 'react';

/**
 * Detects when a live number changes and exposes a temporary highlight window
 * for animations.
 *
 * Change detection happens DURING render (the guarded setState-in-render
 * "derived state" pattern), not in an effect: an effect-based capture commits
 * one frame with the new value before the highlight opens, which paints the
 * final number, snaps back, then rolls - a visible stutter on every live
 * tick. Detecting in render lets React restart before anything paints.
 *
 * Changes below `minDelta` advance the baseline silently (a 0.02 float
 * artifact must not flash a "+0.0"); changes landing while a window is open
 * accumulate, so back-to-back polls show the net delta from the original
 * baseline rather than just the latest tick.
 *
 * @returns `isHighlight` - true for 2s after a change;
 *          `diff` - the accumulated change (stable identity per window);
 *          `prevScore` - the pre-change baseline while highlighted, else the
 *          current value - usable directly as a CountUp start.
 */
export const useScoreChangeHighlight = (currentScore: number, minDelta: number = 0.05) => {
  const prevRef = useRef(currentScore);
  const [captured, setCaptured] = useState<{ diff: number; prev: number } | null>(null);

  if (prevRef.current !== currentScore) {
    const diff = currentScore - prevRef.current;
    const base = prevRef.current;
    prevRef.current = currentScore;
    setCaptured(c => {
      if (c) return { diff: c.diff + diff, prev: c.prev };
      return Math.abs(diff) >= minDelta ? { diff, prev: base } : null;
    });
  }

  useEffect(() => {
    if (!captured) return;
    const timer = setTimeout(() => setCaptured(null), 2000);
    return () => clearTimeout(timer);
  }, [captured]);

  const diff = captured?.diff ?? 0;
  return { isHighlight: captured !== null, diff, prevScore: captured ? captured.prev : currentScore };
};
